/**
 * Maps ZOE capability / tool names to fleet-manager friendly step labels.
 * Internal tool names still appear in the monospace tool-tag on the active step.
 */

const TOOL_LABELS: Record<string, string> = {
  resolve_identifiers: 'Identifying vehicles, drivers, and orders',
  resolve_vehicle: 'Identified vehicle',
  resolve_order: 'Identified order',
  resolve_driver: 'Identified driver',
  resolve_customer: 'Identified customer',
  get_entity_aliases: 'Checking known identifiers',
  get_vehicle_timeline: 'Fetching vehicle timeline',
  get_entity_events: 'Fetching related events',
  get_order_evidence: 'Checking orders and evidence',
  get_driver_compliance: 'Checking driver compliance',
  get_operational_anomalies: 'Scanning for operational issues',
  get_fleet_violation_summary: 'Checking fleet violations',
  get_data_reliability: 'Checking data reliability',
  query_graph_neighbours: 'Tracing related entities',
  get_revenue_by_day: 'Pulling revenue by day',
  get_order_volume_by_day: 'Pulling order volume by day',
  get_customer_orders: 'Looking up customer orders',
  get_vehicle_fuel_summary: 'Summarising vehicle fuel spend',
  get_vehicle_orders_summary: 'Summarising vehicle orders',
  get_driver_activity_summary: 'Summarising driver activity',
  get_orders_by_location: 'Looking up orders by location',
  list_live_vehicles: 'Checking live vehicle status',
  list_fleet_assets: 'Loading Assets vehicle roster',
  get_fleet_config: 'Reading fleet configuration',
  get_utilisation_summary: 'Pulling utilisation summary',
  get_utilisation_profit_insight: 'Joining utilisation and profit',
  invoke_profit_agent: 'Asking Profit Agent',
  get_period_summary: 'Pulling period summary',
  compare_periods: 'Comparing periods',
  get_vehicle_trend: 'Checking vehicle trend',
  get_customer_analysis: 'Analysing customers',
  generate_monday_brief: 'Building Monday brief',
};

const PROFIT_TOOL_LABELS: Record<string, string> = {
  get_period_summary: 'Pulling period summary',
  compare_periods: 'Comparing periods',
  get_vehicle_trend: 'Checking vehicle trend',
  get_customer_analysis: 'Analysing customers',
  generate_monday_brief: 'Building Monday brief',
};

export function toolStepLabel(tool: string): string {
  if (tool.startsWith('profit_agent/')) {
    const inner = tool.replace('profit_agent/', '');
    return PROFIT_TOOL_LABELS[inner] ?? `Profit: ${inner.replace(/_/g, ' ')}`;
  }
  return TOOL_LABELS[tool] ?? humanizeToolName(tool);
}

export function humanizeToolName(tool: string): string {
  return tool.replace(/^profit_agent\//, '').replace(/_/g, ' ');
}

/** Typical investigation duration ranges shown in the thinking footer. */
export function typicalDurationRange(
  questionType?: string,
): { min: number; max: number } {
  switch (questionType) {
    case 'fleet_aggregation':
      return { min: 4, max: 7 };
    case 'compliance_check':
      return { min: 5, max: 10 };
    case 'comparison':
      return { min: 5, max: 9 };
    case 'entity_traversal':
      return { min: 5, max: 8 };
    default:
      return { min: 5, max: 8 };
  }
}

/** Short meta line from a tool args preview (truncate for the card). */
export function formatArgsMeta(argsPreview: string | undefined): string | undefined {
  if (!argsPreview?.trim()) return undefined;
  const cleaned = argsPreview.replace(/\s+/g, ' ').trim();
  if (cleaned.length <= 72) return cleaned;
  return `${cleaned.slice(0, 69)}…`;
}

export function formatToolResultMeta(claims: number, limitations: number, cached: boolean): string {
  const parts: string[] = [];
  if (claims > 0) parts.push(`${claims} finding${claims === 1 ? '' : 's'}`);
  if (limitations > 0) parts.push(`${limitations} limitation${limitations === 1 ? '' : 's'}`);
  if (cached) parts.push('cached');
  return parts.length > 0 ? parts.join(' · ') : 'Complete';
}
